/**
 * Helpers for app-issued session JWTs (external email-code users).
 *
 * Internal staff carry MSAL tokens (refreshed silently by MSAL); external users
 * carry a JWT the backend signs itself, valid for its full lifetime (~8h) with
 * no MSAL refresh. These helpers let the auth layer treat an app token as a
 * valid session for its own lifetime instead of the MSAL 15-min staleness model.
 */
import { APP_JWT_ISSUER } from "../constants/auth";
import { getAccessTokenCookie } from "./cookies";

/** Must match the backend EXTERNAL_JWT_ISSUER. Single source: constants/auth. */
export { APP_JWT_ISSUER };

interface AppTokenClaims {
  iss?: string;
  exp?: number; // seconds since epoch
  email?: string;
  user_type?: string;
}

/** Decode a JWT payload without verifying (verification is the backend's job). */
const decodeJwt = (token: string): AppTokenClaims | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as AppTokenClaims;
  } catch {
    return null;
  }
};

/** Is this token one we issued (vs an MSAL/Azure AD token)? */
export const isAppToken = (token: string | null): boolean => {
  if (!token) return false;
  return decodeJwt(token)?.iss === APP_JWT_ISSUER;
};

/**
 * Whether the current in-memory token is an app-issued token that hasn't
 * expired per its own `exp` claim.
 */
export const hasValidAppToken = (): boolean => {
  const token = getAccessTokenCookie();
  const claims = token ? decodeJwt(token) : null;
  if (!claims || claims.iss !== APP_JWT_ISSUER || !claims.exp) return false;
  return claims.exp * 1000 > Date.now();
};
